import { Button, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useLocation, useParams } from 'react-router-dom';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getTestCycleKeyFromLocation } from '../../../../lib/executionUtils';
import { generateNotificationUrl } from '../../../../lib/notificationUtils';
import { getIssueMetadata, getIssuePriorities, getIssueTypes, getJiraLabels } from '../../../../modules/jira';
import FilePreview from '../../FilePreview';
import Modal from '../../Modal';
import TextInput from '../../TextInput';
import FileUploadJira from '../attachments/FileUploadJira';
import JiraIssueLinkage from './JiraIssueLinkage';
import JiraUserSelector from './JiraUserSelector';
import LinkedProjectSelector from './LinkedProjectSelector';
import MultiSelectDropdown from '../../MultiSelectDropdown';
const MAX_ATTACHMENTS = 10;

const CreateJiraIssueForm = ({
  show,
  handleClose,
  fetchLinkedIssues,
  entityId,
  entityType,
  currentProject,
  testCaseData,
  entityKey,
  linkedJiraProjects,
  prefillData = null,
  setPrefillData = () => { },
  selectedProjectFromSearch = null,
}) => {
  const mounted = useRef(true);
  const { addNotification } = useNotification();
  const location = useLocation();
  const { projectId } = useParams();
  const testCycleKey = getTestCycleKeyFromLocation(location);

  const [formData, setFormData] = useState({
    summary: '',
    description: '',
    issueType: '',
    assignee: '',
    reporter: '',
    priority: '',
  });
  const [jiraProjectId, setJiraProjectId] = useState(null);
  const [selectedLinkedProject, setSelectedLinkedProject] = useState(null);
  const [issueTypes, setIssueTypes] = useState([]);
  const [priorities, setPriorities] = useState([]);
  const [showPriorityField, setShowPriorityField] = useState(false);
  const [labels, setLabels] = useState([]);
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [isLoadingLabels, setIsLoadingLabels] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [issueKey, setIssueKey] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    mounted.current = true;
    return () => (mounted.current = false);
  }, []);

  useEffect(() => {
    if (prefillData) {
      setFormData(prev => ({
        ...prev,
        summary: prefillData.summary || prev.summary,
        description: prefillData.description || prev.description,
      }));
      setAttachments(prefillData?.attachments?.length > 0 ? prefillData?.attachments : []);
    }
  }, [prefillData]);

  const checkPriorityFieldVisibility = useCallback(
    async (linkedProjectId, issueTypeId) => {
      try {
        const result = await getIssueMetadata(linkedProjectId, issueTypeId, projectId || currentProject.id);
        if (mounted.current) setShowPriorityField(Boolean(result.priority));
      } catch {
        if (mounted.current) setShowPriorityField(false);
      }
    },
    [projectId, currentProject?.id]
  );

  const fetchJiraLabels = useCallback(
    async (linkedProjectId) => {
      if (!linkedProjectId) return;
      setIsLoadingLabels(true);
      try {
        const result = await getJiraLabels(projectId || currentProject.id);
        if (mounted.current) {
          const formattedLabels = (result.labels || []).map(label => ({
            id: label,
            name: label,
          }));
          setLabels(formattedLabels);
        }
      } catch (error) {
        console.error('Failed to fetch Jira labels:', error);
        if (mounted.current) setLabels([]);
      } finally {
        if (mounted.current) setIsLoadingLabels(false);
      }
    },
    [projectId, currentProject?.id]
  );

  const fetchCreateIssueData = useCallback(
    async (linkedProjectId) => {
      if (!linkedProjectId) return;
      setLoading(true);
      setJiraProjectId(linkedProjectId);

      try {
        const [types, priorityList] = await Promise.all([
          getIssueTypes(linkedProjectId, projectId),
          getIssuePriorities(linkedProjectId, projectId),
        ]); 
        fetchJiraLabels(linkedProjectId);       
        if (!mounted.current) return;

        setIssueTypes(types || []);
        setPriorities(priorityList || []);

        const defaultType = types?.find(t => t.name === 'Bug') || types?.[0];
        const defaultPriority = priorityList?.find(p => p.name === 'Medium') || priorityList?.[0];

        setFormData(prev => ({
          ...prev,
          issueType: defaultType?.id || '',
          priority: defaultPriority?.id || '',
        }));

        if (defaultType?.id) {
          await checkPriorityFieldVisibility(linkedProjectId, defaultType.id);
        }
      } catch {
        if (mounted.current) setIssueTypes([]);
      } finally {
        if (mounted.current) setLoading(false);
      }
    },
    [projectId, checkPriorityFieldVisibility, fetchJiraLabels]
  );

  useEffect(() => {
    if (show && linkedJiraProjects?.length > 0 && !selectedLinkedProject) {
      const first =  selectedProjectFromSearch || linkedJiraProjects[0];
      setSelectedLinkedProject(first);
      fetchCreateIssueData(first.linked_project_id);
    }
  }, [show, linkedJiraProjects, selectedLinkedProject, selectedProjectFromSearch, fetchCreateIssueData]);

  const handleProjectChange = useCallback(
    (project) => {
      setSelectedLinkedProject(project);
      setShowPriorityField(false);
      setPriorities([]);
      setLabels([]);
      setSelectedLabels([]);
      setFormData(prev => ({ ...prev, issueType: '', priority: '' }));
      fetchCreateIssueData(project?.linked_project_id);
    },
    [fetchCreateIssueData]
  );

  const handleInputChange = useCallback((id, value) => {
    setFormData(prev => ({ ...prev, [id]: value }));
  }, []);

  const handleDropdownChange = useCallback(
    (id, value) => {
      setFormData(prev => ({ ...prev, [id]: value || null }));
      if (id === 'issueType' && value && jiraProjectId) {
        checkPriorityFieldVisibility(jiraProjectId, value);
      }
    },
    [jiraProjectId, checkPriorityFieldVisibility]
  );

  const handleLabelsChange = useCallback((selectedIds) => {
    setSelectedLabels(selectedIds);
  }, []);

  const handleFiles = (files) => {
    if (attachments?.length + files.length > MAX_ATTACHMENTS) {
      setError('');
      setError(`You can only upload a total of ${MAX_ATTACHMENTS} files.`);
      return;
    }
    setAttachments(prev => [...prev, ...files]);
    setError('');
  };

  const handleRemoveImage = (index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.summary.trim()) return addNotification('danger', 'Please provide a summary');
    if (!formData.issueType) return addNotification('danger', 'Please select an Issue Type');
    if (!formData.reporter) return addNotification('danger', 'Please select a Reporter');
    if (showPriorityField && !formData.priority) return addNotification('danger', 'Please select a Priority');

    const otherData = entityType === 'Execution' ? { testCaseKey: testCaseData?.key, testCycleKey } : null;
    const entityUrl = generateNotificationUrl(entityType, projectId, entityKey, 'traceability', otherData);

    const issuePayload = {
      fields: {
        summary: formData.summary.trim(),
        issuetype: { id: formData.issueType },
        project: { id: jiraProjectId },
        reporter: { id: formData.reporter },
        ...(showPriorityField ? { priority: { id: formData.priority } } : {}),
        ...(formData.assignee ? { assignee: { id: formData.assignee } } : {}),
        ...(formData.description ? { description: formData.description } : {}),
        ...(selectedLabels.length > 0 ? { labels: selectedLabels } : {}),
      },
      entityUrl,
      entityKey,
      entityType,
      entityId,
      attachments: prefillData?.attachments?.length > 0 ? prefillData?.attachments?.map(a => a.url) : [],
    };

    const unifiedFormData = new FormData();
    unifiedFormData.append('issueData', JSON.stringify(issuePayload));
    const filesToUpload = attachments?.filter(file => file instanceof File || file instanceof Blob);

    if (filesToUpload.length > 0) {
      filesToUpload.forEach((file) => {
        unifiedFormData.append('files', file);
      });
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const apiRoot = process.env.REACT_APP_API_URL || '';

      const response = await fetch(`${apiRoot}/api/v1/jira/create-issue`, {
        method: 'POST',
        body: unifiedFormData,
        headers: {
          'Authorization': `${token}`,
          'X-Project-Id': projectId,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData?.error?.message || 'Failed to create the Jira issue.';
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      const key = responseData?.jira?.key;

      fetchLinkedIssues();
      addNotification('success', 'Issue added successfully.');

      if (entityType === 'Execution') {
        setIssueKey(key);
        setStep(2);
      } else {
        handleModalClose();
      }
    } catch (error) {
      addNotification('danger', error.message || 'Failed to create the Jira issue.');
    } finally {
      if (mounted.current) setSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (submitting) return;
    setStep(1);
    setIssueKey('');
    setFormData({ summary: '', description: '', issueType: '', assignee: '', reporter: '', priority: '' });
    setAttachments([]);
    setSelectedLinkedProject(null);
    setSelectedLabels([]);
    setPrefillData(null);
    handleClose();
  };


  return (
    <Modal
      open={show}
      handleClose={handleModalClose}
      width="sm"
      modalTitle={step === 1 ? 'Create & Link Jira Issue' : 'Create Issue Links'}
      action={() => (
        <>
          <Button variant="outlined" color="secondary" onClick={handleModalClose} disabled={submitting}>Close</Button>
          {step === 1 && (
            <Button variant="contained" onClick={handleSubmit} disabled={submitting || !formData.summary || !formData.issueType || !formData.reporter || (showPriorityField && !formData.priority)}>
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          )}
        </>
      )}
    >
      {loading || submitting ? (
        <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: 200 }}>
          <Spinner />
          {submitting && <div className="mt-4">Creating...</div>}
        </div>
      ) : step === 1 ? (
        <Form onSubmit={handleSubmit}>
          <Container fluid>
            <Row className="mb-2">
              <Col sm={6}>
                <Form.Label className="required">Project</Form.Label>
                <LinkedProjectSelector
                  linkedProjects={linkedJiraProjects}
                  selectedProjectId={selectedLinkedProject?.linked_project_id}
                  onProjectChange={handleProjectChange}
                />
              </Col>
              <Col sm={6}>
                <Form.Label className="required">Issue Type</Form.Label>
                <Form.Select value={formData.issueType} onChange={(e) => handleDropdownChange('issueType', e.target.value)}>
                  {issueTypes.map(type => <option key={type.id} value={type.id}>{type.name}</option>)}
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <Form.Label className="required">Summary</Form.Label>
                <TextInput id="summary" value={formData.summary} onChange={handleInputChange} required />
              </Col>
            </Row>

            <Row className="mb-2">
              <Col>
                <Form.Label>Description</Form.Label>
                <TextInput id="description" type="textarea" rows={3} value={formData.description} onChange={handleInputChange} />
              </Col>
            </Row>

            <Row className="mt-3">
              <Col sm={6}>
                <Form.Label className="required">Reporter</Form.Label>
                <JiraUserSelector value={formData.reporter} onChange={(v) => handleDropdownChange('reporter', v)} projectId={projectId} jiraProjectId={jiraProjectId} />
              </Col>
              <Col sm={6}>
                <Form.Label>Assignee</Form.Label>
                <JiraUserSelector value={formData.assignee} onChange={(v) => handleDropdownChange('assignee', v)} projectId={projectId} jiraProjectId={jiraProjectId} />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col sm={6}>
                <Form.Label>Labels</Form.Label>
                {isLoadingLabels ? (
                  <div className="text-muted">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Loading labels...
                  </div>
                ) : (
                  <MultiSelectDropdown
                    options={labels}
                    selectedValues={selectedLabels}
                    placeholder="Select labels"
                    onChange={handleLabelsChange}
                    showSelectAll={false}
                    name="labels"
                    sx={{ mb: 0 }}
                  />
                )}
              </Col>
               {showPriorityField && (
                <Col sm={6}>
                  <Form.Label className="required">Priority</Form.Label>
                  <Form.Select value={formData.priority} onChange={(e) => handleDropdownChange('priority', e.target.value)}>
                    {priorities.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </Form.Select>
                </Col>            
            )}
            </Row>         

            <Row className="mt-3">
              <Col>
                <Form.Label>Attachments ({attachments?.length}/{MAX_ATTACHMENTS})</Form.Label>
                <FileUploadJira enableDragAndDrop handleFiles={handleFiles} />
                <FilePreview attachments={attachments} handleRemoveImage={handleRemoveImage} submitting={submitting} />
                {error && <Typography className="text-danger" variant="caption">{error}</Typography>}
              </Col>
            </Row>
          </Container>
        </Form>
      ) : (
        <JiraIssueLinkage testCaseId={testCaseData?.id} issueKey={issueKey} projectId={projectId} handleModalClose={handleModalClose} type="create" setSubmitting={setSubmitting} />
      )}
    </Modal>
  );
};

export default CreateJiraIssueForm;
